import { ButtonHTMLAttributes, MouseEventHandler } from 'react';
import classnames from 'classnames';

import Link from '@/components/renderers/Link';

type HTMLButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'size' | 'onClick'
>;

type Size = 'small' | 'large' | 'regular';
type Appearance = 'minimal' | 'outline' | 'regular';
export type Colour =
  | 'dark'
  | 'grey'
  | 'pink'
  | 'purple'
  | 'white'
  | 'blue'
  | 'royal'
  | 'whisper'
  | 'faded-cornflower';

interface ButtonProps extends HTMLButtonProps {
  size?: Size;
  appearance?: Appearance;
  hover?: Appearance;
  colour?: Colour;
  block?: boolean;
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  withArrow?: boolean;
}

function ArrowSVG({ className, size }: { className?: string; size: Size }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={classnames(className, {
        'w-[14px] h-[14px]': size === `small`,
        'w-4 h-4': size === `regular`,
        'w-5 h-5': size === `large`,
      })}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.4714 3.52876C10.2111 3.26841 9.78898 3.26841 9.52865 3.52876C9.26831 3.78911 9.26831 4.21122 9.52865 4.47157L12.3906 7.3335H2.00004C1.63185 7.3335 1.33337 7.63196 1.33337 8.00016C1.33337 8.36836 1.63185 8.66683 2.00004 8.66683H12.3906L9.52865 11.5288C9.26831 11.7891 9.26831 12.2112 9.52865 12.4716C9.78898 12.7319 10.2111 12.7319 10.4714 12.4716L14.469 8.47402C14.4772 8.46593 14.4851 8.45763 14.4929 8.44913C14.5429 8.39432 14.5821 8.33317 14.6105 8.26843C14.6467 8.18635 14.6667 8.0956 14.6667 8.00016C14.6667 7.90473 14.6467 7.81398 14.6105 7.73189C14.5821 7.66715 14.5429 7.60601 14.4929 7.55119C14.4851 7.54269 14.4772 7.53439 14.469 7.52631L10.4714 3.52876Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function Button({
  block = false,
  children,
  className,
  colour = `grey`,
  disabled = false,
  href,
  hover,
  onClick,
  size = `regular`,
  appearance = `regular`,
  withArrow,
  ...rest
}: ButtonProps) {
  const hoverColour = hover || colour;

  const hoverClass = {
    regular: {
      dark: `hover:bg-transparent hover:text-haiti`,
      grey: `hover:bg-transparent`,
      pink: `hover:bg-radical-rose hover:text-white`,
      purple: `hover:bg-radical-cornflower hover:text-white`,
      white: `hover:bg-white hover:text-haiti`,
      whisper: `hover:bg-white hover:text-haiti`,
      blue: `hover:bg-science-blue hover:text-deep-koamaru`,
      royal: `hover:bg-transparent hover:text-royal-blue`,
      'faded-cornflower': `hover:bg-fog hover:text-corflower`,
    },
    minimal: {
      dark: ``,
      grey: ``,
      pink: ``,
      purple: ``,
      white: ``,
      whisper: ``,
      blue: ``,
      royal: ``,
      'faded-cornflower': ``,
    },
    outline: {
      dark: `hover:bg-haiti hover:text-white`,
      grey: `hover:bg-primary-100`,
      pink: `hover:bg-rose hover:text-white`,
      purple: `hover:bg-cornflower hover:text-white`,
      white: ``,
      whisper: ``,
      blue: `hover:bg-deep-koamaru hover:text-white`,
      royal: `hover:bg-royal-blue hover:text-white`,
      'faded-cornflower': `hover:bg-faded-cornflower hover:text-corflower`,
    },
  };

  const styleClass = {
    regular: {
      dark: `bg-haiti border-transparent text-white`,
      grey: `bg-primary-100 border-transparent text-haiti`,
      pink: `bg-rose border-transparent text-white`,
      purple: `bg-cornflower border-transparent text-white`,
      white: `bg-white border-transparent text-haiti`,
      whisper: `bg-white border-transparent text-haiti`,
      blue: `bg-deep-koamaru border-transparent text-white`,
      royal: `bg-royal-blue border-transparent text-white`,
      'faded-cornflower': `bg-faded-cornflower border-transparent text-cornflower`,
    },
    minimal: {
      dark: `bg-transparent border-transparent text-haiti`,
      grey: `bg-transparent border-transparent text-primary-100`,
      pink: `bg-transparent border-transparent text-rose`,
      purple: `bg-transparent border-transparent text-cornflower`,
      white: `bg-transparent border-transparent text-white`,
      whisper: `bg-transparent border-transparent text-whisper`,
      blue: `bg-transparent border-transparent text-deep-koamaru`,
      royal: `bg-transparent border-transparent text-royal-blue`,
      'faded-cornflower': `bg-transparent border-transparent`,
    },
    outline: {
      dark: `bg-transparent border-haiti text-haiti`,
      grey: `bg-transparent border-primary-100 text-haiti`,
      pink: `bg-transparent border-rose text-rose`,
      purple: `bg-transparent border-cornflower text-cornflower`,
      white: `bg-transparent border-white text-white`,
      whisper: `bg-transparent border-whisper text-whisper`,
      blue: `bg-transparent border-deep-koamaru text-deep-koamaru`,
      royal: `bg-transparent border-royal-blue text-royal-blue`,
      'faded-cornflower': `bg-transparent border-faded-cornflower text-cornflower`,
    },
  };

  const appearanceVal = (appearanceParam: Appearance) => {
    switch (appearance) {
      case `minimal`:
      case `outline`:
      case `regular`:
        return appearanceParam;
      default:
        return `regular`;
    }
  };

  const colourVal = (colourParam: Colour) => {
    switch (colourParam) {
      case `dark`:
      case `grey`:
      case `pink`:
      case `purple`:
      case `white`:
      case `blue`:
      case `royal`:
      case `whisper`:
      case `faded-cornflower`:
        return colourParam;
      default:
        return `grey`;
    }
  };

  const sizeClass = (sizeParam: Size) => {
    switch (sizeParam) {
      case `small`:
        return `px-2 py-1 text-xs md:text-sm`;
      case `large`:
        return `px-6 py-2`;
      case `regular`:
      default:
        return `px-4 py-2 text-sm`;
    }
  };

  const buttonClass = classnames(
    `border font-semibold inline-block rounded text-center transition-all`,
    appearance !== `minimal` && sizeClass(size),
    styleClass[appearanceVal(appearance)][colourVal(colour)],
    hoverClass[appearanceVal(appearance)][colourVal(hoverColour as Colour)],
    className,
    {
      'cursor-not-allowed opacity-50': disabled,
      'w-full': block,
      'flex items-center': withArrow,
    },
  );

  if (href) {
    return (
      <Link href={href} className={buttonClass} onClick={onClick}>
        {children}
        {withArrow && <ArrowSVG size={size} className="ml-2" />}
      </Link>
    );
  }

  return (
    <button
      className={buttonClass}
      onClick={onClick}
      disabled={disabled}
      type="submit"
      {...rest}
    >
      {children}
      {withArrow && <ArrowSVG size={size} className="ml-2" />}
    </button>
  );
}
